import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import voiceService, { VoiceCreateRequest } from '../../service/voice.service';
import { VoiceEntity } from '../../types/entities';

const emptyForm: VoiceCreateRequest = {
  name: '',
  gender: 'man',
  provider: 'openai',
  externalVoiceId: '',
  languageCode: 'de',
  description: '',
  sampleAudioPath: '',
  isActive: true,
};

const Voices: React.FC = () => {
  const [rows, setRows] = React.useState<VoiceEntity[] | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [form, setForm] = React.useState<VoiceCreateRequest>(emptyForm);
  const [saving, setSaving] = React.useState(false);
  const providerOptions = ['elevenlabs', 'openai', 'deepgram'];
  const [providerMode, setProviderMode] = React.useState<'preset' | 'custom'>('preset');
  const [elevenVoices, setElevenVoices] = React.useState<{ id: string; name: string }[] | null>(null);
  const [elevenLoading, setElevenLoading] = React.useState(false);
  const [ttsOpen, setTtsOpen] = React.useState(false);
  const [ttsVoiceId, setTtsVoiceId] = React.useState<string | null>(null);
  const [ttsText, setTtsText] = React.useState('');
  const [ttsLang, setTtsLang] = React.useState('');
  const [ttsSaving, setTtsSaving] = React.useState(false);
  const [ttsError, setTtsError] = React.useState<string | null>(null);
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const [playingUrl, setPlayingUrl] = React.useState<string | null>(null);
  const togglePlay = (src: string) => {
    try {
      if (playingUrl === src) {
        if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.currentTime = 0;
          audioRef.current = null;
        }
        setPlayingUrl(null);
        return;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
        audioRef.current = null;
      }
      const audio = new Audio(src);
      audioRef.current = audio;
      setPlayingUrl(src);
      audio.onended = () => {
        setPlayingUrl(null);
        audioRef.current = null;
      };
      audio.play().catch(() => {
        setPlayingUrl(null);
        audioRef.current = null;
      });
    } catch {}
  };

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await voiceService.getVoices();
      setRows(data || []);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load voices');
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm);
    setProviderMode('preset');
    setDialogOpen(true);
  };
  const openEdit = (v: VoiceEntity) => {
    setEditingId(v.id);
    setForm({
      name: v.name,
      gender: v.gender,
      provider: v.provider ?? '',
      externalVoiceId: v.externalVoiceId ?? '',
      languageCode: v.languageCode ?? '',
      description: v.description ?? '',
      sampleAudioPath: v.sampleAudioPath ?? '',
      isActive: v.isActive,
    });
    setProviderMode(providerOptions.includes((v.provider || '').toLowerCase()) ? 'preset' : 'custom');
    setDialogOpen(true);
  };
  const closeDialog = () => {
    if (saving) return;
    setDialogOpen(false);
  };

  const save = async () => {
    if (!form.name) return;
    setSaving(true);
    try {
      const payload: VoiceCreateRequest = {
        ...form,
        provider: form.provider || null,
        externalVoiceId: form.externalVoiceId || null,
        languageCode: form.languageCode || null,
        description: form.description || null,
        sampleAudioPath: form.sampleAudioPath || null,
      };
      if (editingId) {
        await voiceService.updateVoice(editingId, payload);
      } else {
        await voiceService.createVoice(payload);
      }
      setDialogOpen(false);
      await load();
    } catch (e) {
      // ignore for now; could show alert
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Silmek istiyor musunuz?')) return;
    try {
      await voiceService.deleteVoice(id);
      await load();
    } catch {}
  };

  React.useEffect(() => {
    if (dialogOpen && (form.provider || '').toLowerCase() === 'elevenlabs') {
      setElevenLoading(true);
      voiceService
        .getElevenLabsVoices()
        .then((list) => setElevenVoices(list || []))
        .catch(() => setElevenVoices([]))
        .finally(() => setElevenLoading(false));
    }
  }, [dialogOpen, form.provider]);

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
        <Button variant="contained" onClick={openCreate}>Create Voice</Button>
        <IconButton onClick={load} aria-label="refresh" size="small"><RefreshIcon /></IconButton>
      </Stack>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Gender</TableCell>
              <TableCell>Provider</TableCell>
              <TableCell>Lang</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Sample</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(rows || []).map((v) => (
              <TableRow key={v.id} hover>
                <TableCell>{v.name}</TableCell>
                <TableCell>{v.gender}</TableCell>
                <TableCell>{v.provider || '-'}</TableCell>
                <TableCell>{v.languageCode || '-'}</TableCell>
                <TableCell>{v.description || '-'}</TableCell>
                <TableCell>{v.isActive ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  {v.sampleAudioPath ? (
                    <Stack direction="column" spacing={0.5}>
                      {(v.sampleAudioPath.split('&&&').filter(Boolean) as string[]).map((u, idx) => {
                        const active = playingUrl === u;
                        return (
                          <IconButton key={idx} aria-label={`play-${idx}`} size="small" onClick={() => togglePlay(u)}>
                            {active ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
                          </IconButton>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Typography variant="caption" color="text.secondary">-</Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <IconButton aria-label="tts-example" size="small" onClick={() => {
                    setTtsVoiceId(v.id);
                    setTtsText('');
                    setTtsLang('en');
                    setTtsError(null);
                    setTtsOpen(true);
                  }}>
                    <RecordVoiceOverIcon fontSize="small" />
                  </IconButton>
                  <IconButton aria-label="edit" size="small" onClick={() => openEdit(v)}><EditIcon fontSize="small" /></IconButton>
                  <IconButton aria-label="delete" size="small" onClick={() => remove(v.id)}><DeleteIcon fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {!loading && rows && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary">No voices.</Typography>
                </TableCell>
              </TableRow>
            )}
            {loading && (
              <TableRow>
                <TableCell colSpan={8}>
                  <Typography variant="body2" color="text.secondary">Loading…</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Voice' : 'Create Voice'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {(form.provider || '').toLowerCase() === 'elevenlabs' ? (
              <FormControl fullWidth>
                <InputLabel id="name-eleven-label">Name (ElevenLabs)</InputLabel>
                <Select
                  labelId="name-eleven-label"
                  label="Name (ElevenLabs)"
                  value={form.externalVoiceId || ''}
                  onChange={(e) => {
                    const val = e.target.value as string;
                    const sel = (elevenVoices || []).find((v) => v.id === val);
                    setForm({ ...form, externalVoiceId: val || '', name: sel?.name || '' });
                  }}
                  disabled={elevenLoading}
                >
                  <MenuItem value="">None</MenuItem>
                  {(elevenVoices || []).map((v) => (
                    <MenuItem key={v.id} value={v.id}>{v.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField label="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required fullWidth />
            )}
            <FormControl fullWidth>
              <InputLabel id="gender-label">Gender</InputLabel>
              <Select labelId="gender-label" label="Gender" value={form.gender} onChange={(e) => setForm({ ...form, gender: e.target.value as any })}>
                <MenuItem value="man">man</MenuItem>
                <MenuItem value="woman">woman</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="provider-label">Provider</InputLabel>
              <Select
                labelId="provider-label"
                label="Provider"
                value={providerMode === 'custom' ? '__custom__' : (providerOptions.includes((form.provider || '').toLowerCase()) ? (form.provider as string) : '')}
                onChange={(e) => {
                  const val = e.target.value as string;
                  if (val === '__custom__') {
                    setProviderMode('custom');
                    setForm({ ...form, provider: '' });
                  } else {
                    setProviderMode('preset');
                    if (val.toLowerCase() === 'elevenlabs') {
                      // ensure we use the ElevenLabs Name select exclusively
                      setForm({ ...form, provider: val, name: '', externalVoiceId: '' });
                    } else {
                      // clear any previously selected externalVoiceId from elevenlabs
                      setForm({ ...form, provider: val, externalVoiceId: '' });
                    }
                  }
                }}
              >
                {providerOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                ))}
                <MenuItem value="__custom__">custom</MenuItem>
              </Select>
            </FormControl>
            {providerMode === 'custom' && (
              <TextField label="Custom Provider" value={form.provider || ''} onChange={(e) => setForm({ ...form, provider: e.target.value })} fullWidth />
            )}
            {(form.provider || '').toLowerCase() !== 'elevenlabs' && (
              <TextField label="External Voice ID" value={form.externalVoiceId || ''} onChange={(e) => setForm({ ...form, externalVoiceId: e.target.value })} fullWidth />
            )}
            <TextField label="Language Code" value={form.languageCode || ''} onChange={(e) => setForm({ ...form, languageCode: e.target.value })} fullWidth />
            <TextField label="Description" value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} fullWidth multiline minRows={2} />
            <TextField label="Sample Audio URL" value={form.sampleAudioPath || ''} onChange={(e) => setForm({ ...form, sampleAudioPath: e.target.value })} fullWidth />
            <FormControlLabel
              control={<Checkbox checked={!!form.isActive} onChange={(e) => setForm({ ...form, isActive: e.target.checked })} />}
              label="Active"
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={saving || !form.name}>{editingId ? 'Save' : 'Create'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={ttsOpen} onClose={() => !ttsSaving && setTtsOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create TTS Example</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {ttsError && <Alert severity="error">{ttsError}</Alert>}
            <TextField
              label="Text"
              value={ttsText}
              onChange={(e) => setTtsText(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
            <TextField
              label="Language Code"
              value={ttsLang}
              onChange={(e) => setTtsLang(e.target.value)}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTtsOpen(false)} disabled={ttsSaving}>Cancel</Button>
          <Button
            variant="contained"
            disabled={ttsSaving || !ttsText || !ttsLang || !ttsVoiceId}
            onClick={async () => {
              if (!ttsVoiceId) return;
              setTtsSaving(true);
              setTtsError(null);
              try {
                const row = (rows || []).find((x) => x.id === ttsVoiceId);
                const updated = await voiceService.postVoiceTtsExample({
                  id: ttsVoiceId,
                  voiceId: row?.externalVoiceId || '',
                  text: ttsText,
                  languageCode: ttsLang,
                });
                setRows((prev) => {
                  const list = prev || [];
                  const idx = list.findIndex((x) => x.id === updated.id);
                  if (idx >= 0) {
                    const copy = [...list];
                    copy[idx] = updated as any;
                    return copy;
                  }
                  return list;
                });
                setTtsOpen(false);
              } catch (e: any) {
                setTtsError(e?.response?.data?.message || e?.message || 'Failed to create example');
              } finally {
                setTtsSaving(false);
              }
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Voices;
